import {
  Grid,
  GridColumn,
  GridCustomCellProps,
  GridRowClickEvent,
} from "@progress/kendo-react-grid";
import "@progress/kendo-theme-default/dist/all.css";
import { DetailsWindow } from "components/WarehouseElements/Grid/DetailsWindow/DetailsWindow";
import { BASE_URL_WAREHOUSE } from "constants/api/routeGlobals";
import {
  NOTIFICATION_EVENT,
  STATUS_CHANGED_EVENT,
  WEBSOCKET_CONNECTED_EVENT,
  WEBSOCKET_FAILED_EVENT,
} from "constants/events";
import {
  WAREHOUSE_FIELD_FILTER,
  WEB_SOCKET_URL,
} from "constants/webhooksConstants";
import { useAppDispatch } from "hooks/StoreHooks";
import { OutletContext } from "pages/Home/Home";
import { JSX, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router";
import StompComponent, {
  StompFilter,
} from "services/webSockets/StompComponent";
import { SubscriptionInfos } from "services/webSockets/SubscriptionSocket";
import { selectToken } from "store/Selectors/appStateSelector";
import { setOpenedDetailId } from "store/Slices/gridEventSlice";
import { useAppSelector } from "store/store";
import "./EtatParc.scss";
import { ElapsedRenderer, ModeRenderer, OccupeRenderer, OperationEnCoursRenderer, ReservationRenderer, VerifierLeRenderer } from "./EtatParcRenderer";

const SUBBED_EVENTS_LIST = [STATUS_CHANGED_EVENT, NOTIFICATION_EVENT];
export function useMinuteTicker(): Date {
  const [now, setNow] = useState<Date>(new Date());

  useEffect(() => {
    let interval: NodeJS.Timeout;
    let timeout: NodeJS.Timeout;

    const schedule = () => {
      const current = new Date();

      // ms until next minute (00 seconds)
      const msUntilNextMinute =
        (60 - current.getSeconds()) * 1000 - current.getMilliseconds();

      timeout = setTimeout(() => {
        setNow(new Date());

        // After first alignment, update every 60 seconds
        interval = setInterval(() => {
          setNow(new Date());
        }, 60_000);
      }, msUntilNextMinute);
    };

    schedule();

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);

  return now;
}


type DetailInfo = {
  id: string;
  posTop: number;
  posLeft: number;
};

export function GridEtatParc(currentLocationStoreNb: number): JSX.Element {
  const dispatch = useAppDispatch();
  const authToken = useAppSelector(selectToken);
  const everyMinuteTimer = useMinuteTicker();
  const [parcs, setParcs] = useState<any[]>([]);
  const latestParcs = useRef(parcs);
  const [currentMinutes, setCurrentMinutes] = useState<Date>(new Date());
  const [webSocketError, setWebSocketError] = useState<string | null>(null);
  const [currentDetailId, setCurrentDetailId] = useState<DetailInfo>({
    id: "",
    posTop: 0,
    posLeft: 0,
  });
  const isCurrentDetailsWindowOpen = useAppSelector(
    (state) => state.gridEventSlice.openedDetailId === currentDetailId.id,
  );

  const warehouseWebSocketFilter = useMemo(() => {
    if (currentLocationStoreNb == undefined) return;
    else {
      return {
        field: WAREHOUSE_FIELD_FILTER,
        operator: "==",
        value: currentLocationStoreNb ? currentLocationStoreNb.toString() : "0",
      } as StompFilter;
    }
  }, [currentLocationStoreNb]);

  const subbedEventsAndFilters = useMemo<SubscriptionInfos[]>(
    () => [
      ...SUBBED_EVENTS_LIST.map((event: any) => ({
        subbedEvent: event,
        filters: warehouseWebSocketFilter ? [warehouseWebSocketFilter] : [],
      })),
    ],
    [warehouseWebSocketFilter, currentLocationStoreNb],
  );

  useEffect(() => {
    latestParcs.current = parcs;
  }, [parcs]);

  function handleApplicatEvent(eventMsg: any) {
    if (eventMsg.event?.type === WEBSOCKET_CONNECTED_EVENT) {
      setWebSocketError(null);
    }
    if (eventMsg.event?.type === WEBSOCKET_FAILED_EVENT) {
      setWebSocketError(
        `La connexion pour la mise-à-jour en temps réelle a échoué (${eventMsg.data.code}).`,
      );
    }
    if (eventMsg.event?.type === STATUS_CHANGED_EVENT) {
      const newParcs = latestParcs.current.map((parc) => {
        if (parc.parcCd === eventMsg.data.id) {
          parc.modeOperation = eventMsg.data.modeOperation;
          parc.reservation = eventMsg.data.typeReservation;
          parc.tauxOccupation = eventMsg.data.tauxOccupation;
          parc.operEnAttente = eventMsg.data.operEnAttente;
          parc.verifierLe = eventMsg.data.verifierLe;
          parc.verifierParUsager = eventMsg.data.verifierParUsager;
          parc.uniteDTPlusVieux = eventMsg.data.uniteDTPlusVieux;
          parc.uniteDTPlusJeune = eventMsg.data.uniteDTPlusJeune;
          return parc;
        } else {
          return parc;
        }
      });
      setParcs(newParcs);
    }
  }

  const handleCellClick = (event: GridRowClickEvent) => {
    const posX = event.syntheticEvent.pageX;
    const posY = event.syntheticEvent.pageY;
    if (
      isCurrentDetailsWindowOpen &&
      currentDetailId.id === event.dataItem.parcCd
    ) {
      setCurrentDetailId({ id: "", posTop: 0, posLeft: 0 });
      dispatch(setOpenedDetailId(null));
      return;
    }
    setCurrentDetailId({
      id: event.dataItem.parcCd,
      posTop: posY,
      posLeft: posX,
    });
    dispatch(setOpenedDetailId(event.dataItem.parcCd));
  };

  const fetchData = (storeNb: number) => {
    fetch(BASE_URL_WAREHOUSE + `sge/${storeNb}/suivi`)
      .then((response) => response.json())
      .then((data) => {
        setParcs(data);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement", error);
      });
  };

  const VerifierLeRendererTimerTriggered = (extraProp) => {
    return function CellWrap(props: GridCustomCellProps) {
      return <VerifierLeRenderer {...props} />;
    }

  }

  const ElapsedRendererTimerTriggered = (extraProp) => {
    return function CellWrap(props: GridCustomCellProps) {
      return <ElapsedRenderer {...props} />;
    }

  }

  useEffect(() => {
    fetchData(currentLocationStoreNb);
  }, [currentLocationStoreNb]);

  useEffect(() => {
    setCurrentMinutes(everyMinuteTimer);
  }, [everyMinuteTimer]);

  const resetPopup = () => {
    setCurrentDetailId({ id: "", posTop: 0, posLeft: 0 });
  };

  return (
    <>
      <div
        id="page-etat-parc"
        key={currentLocationStoreNb}
        style={{ padding: "20px", height: "100%", overflow: "hidden" }}
      >
        <StompComponent
          webSocketUrl={WEB_SOCKET_URL}
          authToken={authToken}
          subscriptions={subbedEventsAndFilters}
          handleApplicatEvent={handleApplicatEvent}
        >
          <div>
            {isCurrentDetailsWindowOpen && (
              <DetailsWindow
                key={currentDetailId.id}
                id={currentDetailId.id}
                posTop={currentDetailId.posTop}
                posLeft={currentDetailId.posLeft}
                useStoreNb={currentLocationStoreNb}
              />
            )}
          </div>
          <Grid
            data={parcs}
            style={{ height: "100%" }}
            dataItemKey="parcCd"
            sortable={true}
            pageable={false}
            autoProcessData={true}
            filterable={true}
            onRowClick={handleCellClick}
            onScroll={resetPopup}
            onDataStateChange={resetPopup}
          >
            <GridColumn field="description" title="Parc" width={"150px"} />
            <GridColumn
              field="modeOperation"
              title="Mode"
              cells={{ data: ModeRenderer }}
              width={"150px"}
            />
            <GridColumn
              field="reservation"
              title="Réservation"
              cells={{ data: ReservationRenderer }}
              width={"150px"}
            />
            <GridColumn
              field="tauxOccupation"
              title="Occupé"
              width={"230px"}
              filterable={false}
              cells={{ data: OccupeRenderer }}
            />
            <GridColumn
              field="uniteDTPlusVieux"
              title="Plus ancienne"
              width={"150px"}
              filterable={false}
              cells={{ data: ElapsedRendererTimerTriggered(currentMinutes) }}
            />
            <GridColumn
              field="uniteDTPlusJeune"
              title="Plus récente"
              width={"150px"}
              filterable={false}
              cells={{ data: ElapsedRendererTimerTriggered(currentMinutes) }}
            />
            <GridColumn
              field="verifierLe"
              title="Vérifier le"
              filterable={false}
              cells={{ data: VerifierLeRendererTimerTriggered(currentMinutes) }}
            />
            <GridColumn
              field="operEnAttente"
              title="Opération"
              filterable={false}
              cells={{ data: OperationEnCoursRenderer }}
            />
          </Grid>
        </StompComponent>
      </div>
    </>
  );
}

export function EtatParc(): JSX.Element {
  const { storeNb: currentLocationStoreNb }: OutletContext = useOutletContext();

  return GridEtatParc(currentLocationStoreNb);
}
